/**
 * Error normalization. Pulls a human-readable message and (when present)
 * an X-Request-ID off whatever the caller has — usually an `ApiError`,
 * sometimes a plain `Error`, occasionally a string.
 *
 * DRF error payloads come in several shapes; all are flattened to plain
 * sentences (never raw JSON):
 *   {"detail": "…"}                      → "…"
 *   {"non_field_errors": ["…"]}          → "…"
 *   {"from_date": ["This field is …"]}   → "From date: This field is …"
 *   ["…", "…"]                           → "… …"
 */
import { ApiError } from "@/lib/api";

export interface FormattedError {
  message: string;
  requestId: string | null;
}

const STATUS_MESSAGES: Record<number, string> = {
  400: "The request couldn't be processed. Please check your input and try again.",
  401: "Your session has expired. Please sign in again.",
  403: "You don't have permission to do this. Contact your administrator if you believe you should.",
  404: "The requested record could not be found.",
  409: "This change conflicts with an existing record.",
  429: "Too many requests. Please wait a moment and try again.",
};

function statusMessage(status: number): string {
  const known = STATUS_MESSAGES[status];
  if (known) return known;
  if (status >= 500) {
    return "The server ran into a problem. Please try again in a moment.";
  }
  return `The request failed (error ${status}). Please try again.`;
}

/** "date_of_joining" → "Date of joining" */
function humanizeField(field: string): string {
  const label = field.replace(/[_-]+/g, " ").trim();
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Keys whose contents ARE the message — don't prefix them with a label. */
const PASSTHROUGH_KEYS = new Set([
  "detail",
  "details",
  "non_field_errors",
  "error",
  "errors",
  "message",
]);

/** Recursively pull user-facing sentences out of a DRF-style payload. */
function messagesFromPayload(payload: unknown): string[] {
  if (payload === null || payload === undefined) return [];
  if (typeof payload === "string") {
    const text = payload.trim();
    // An HTML error page (e.g. a proxy 502) is not something to show users.
    if (!text || text.startsWith("<")) return [];
    return [text];
  }
  if (Array.isArray(payload)) {
    return payload.flatMap(messagesFromPayload);
  }
  if (typeof payload === "object") {
    return Object.entries(payload as Record<string, unknown>).flatMap(
      ([key, value]) => {
        const inner = messagesFromPayload(value);
        if (PASSTHROUGH_KEYS.has(key)) return inner;
        return inner.map((msg) => `${humanizeField(key)}: ${msg}`);
      },
    );
  }
  return [];
}

/** Browser fetch failures surface as TypeError("Failed to fetch") et al. */
function isNetworkError(err: Error): boolean {
  return /failed to fetch|networkerror|load failed/i.test(err.message);
}

export function formatError(err: unknown): FormattedError {
  if (err instanceof ApiError) {
    // 401 payloads are token internals — SimpleJWT answers with "Given token
    // not valid for any token type", `code: token_not_valid` and a nested
    // `messages` array. None of that helps a user, and it reads as a crash in
    // a toast. The advice is the same whatever the body says.
    const messages =
      err.status === 401 ? [] : messagesFromPayload(err.payload);
    const message = messages.length
      ? messages.join(" ")
      : statusMessage(err.status);
    return { message, requestId: err.requestId };
  }
  if (err instanceof Error) {
    if (isNetworkError(err)) {
      return {
        message:
          "Couldn't reach the server. Check your internet connection and try again.",
        requestId: null,
      };
    }
    return { message: err.message, requestId: null };
  }
  return { message: String(err), requestId: null };
}
